import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { some, without } from 'lodash';

import { useIsLoading } from '../../page-framework/panel/Panel';

const LoadingContext = createContext();

/** @typedef {{ isLoading: boolean }} LoadingWrapper */

/**
 * Context provider to merge multiple "loads" from child components and synchronize them into
 * one load which signals the panel when we have started or finally finished loading.
 *
 * This component is only used in the Map at the moment, but if we have other panels using this
 * pattern, this component should be moved out into the page framework (or it's own folder) and
 * used there. There is nothing coupling this functionalty directly to the map, it's just unnecessary
 * to have this live in the page framework if it's only used in one place.
 */
export function MapPanelLoadingProvider(props) {
    const { children } = props;
    const [, /* Not used */ setIsLoading] = useIsLoading();
    const [loadingItems, setLoadingItems] = useState([]);

    const addLoadingSource = useCallback((/** @type {LoadingWrapper} */ src) => {
        setLoadingItems((srcs) => [...srcs, src]);
    }, []);

    const removeLoadingSource = useCallback((/** @type {LoadingWrapper} */ src) => {
        setLoadingItems((srcs) => without(srcs, src));
    }, []);

    const onUpdate = useCallback(() => {
        const isLoading = some(loadingItems, (l) => l.isLoading === true);

        if (typeof setIsLoading === 'function') {
            setIsLoading(isLoading);
        }
    }, [setIsLoading, loadingItems]);

    return (
        <LoadingContext.Provider value={{ addLoadingSource, removeLoadingSource, onUpdate }}>
            {children}
        </LoadingContext.Provider>
    );
}

/**
 * Returns a callback which is used to signal to a parent that a child container is loading
 *
 * @param {boolean} isLoading
 */
export function useSetIsLoading(isLoading) {
    /** @type {import('react').MutableRefObject<LoadingWrapper>} */
    const isLoadingRef = useRef({ isLoading: null });
    const { addLoadingSource, removeLoadingSource, onUpdate } = useContext(LoadingContext);

    useEffect(() => {
        const isLoadingWrapper = isLoadingRef.current;
        addLoadingSource(isLoadingWrapper);

        return () => {
            removeLoadingSource(isLoadingWrapper);
        };
    }, [addLoadingSource, removeLoadingSource]);

    useEffect(() => {
        isLoadingRef.current.isLoading = isLoading;
        onUpdate();
    }, [isLoading, onUpdate]);
}
